// Interfacce per la configurazione dinamica degli asset
export interface AssetFieldConfig {
  uid?: string; // Identificatore univoco dell'attributo, utile per custom KPIs
  type: string;
  label: string;
  visible?: boolean; // Proprietà opzionale per la visibilità

  // Nuovi campi per supportare il catalogo
  scope?: string; // signal, platform, tcu, erp, kpi, composite_signal, composite_kpi, table
  unit?: string; // unità di misura (km/h, rpm, l, etc.)
  catalogType?: string; // tipo originale dal catalogo (Number, String, boolean, etc.)

  // Campi per gestire attributi comuni vs specifici del modello
  source?: 'common' | 'model'; // indica se l'attributo è comune o specifico del modello
  availableInModels?: string[]; // lista dei modelli che supportano questo attributo (solo per source='model')

  // Campi per le traduzioni multilingua
  i18nLabel?: { [key: string]: string }; // traduzioni del label per diverse lingue (es: { 'it-IT': 'Velocità', 'en-EN': 'Speed' })

  // Nuovi campi per le destinazioni di visualizzazione
  showInPopup?: boolean; // Mostra nel popup del marker sulla mappa
  showInSidebar?: boolean; // Mostra nella sidebar
  showInDetails?: boolean; // Mostra nel pannello dettagli

  // Campo per l'ordine di visualizzazione
  displayOrder?: number; // Ordine di visualizzazione all'interno dello scope
}

export interface AssetMappingConfig {
  version: string;
  lastUpdated: string;
  description: string;
  audience?: 'USER' | 'CUSTOMER' | 'GLOBAL';
  idCustomer?: string;
  fields: Record<string, AssetFieldConfig>;
}

/**
 * mappingConfig.fields è indicizzato per "scope|name" (nuovo schema). Le config salvate prima di
 * questo cambio usano solo "name" come chiave: buildFieldConfigKey/getFieldConfig gestiscono il
 * fallback automaticamente così le config vecchie restano leggibili. Usare SEMPRE questi helper
 * (mai indicizzare mappingConfig.fields[name] direttamente) per evitare che due catalog item con lo
 * stesso name ma scope diverso (es. "ad" come scope "signal" e come scope "kpi") condividano la
 * stessa configurazione (visible/showInPopup/showInSidebar/showInDetails/displayOrder).
 */
export function buildFieldConfigKey(scope: string, name: string): string {
  return `${scope}|${name}`;
}

export function getFieldConfig(
  mappingConfig: AssetMappingConfig | null | undefined,
  scope: string,
  name: string
): AssetFieldConfig | undefined {
  if (!mappingConfig?.fields) return undefined;
  return mappingConfig.fields[buildFieldConfigKey(scope, name)] ?? mappingConfig.fields[name];
}

export function setFieldConfig(
  mappingConfig: AssetMappingConfig,
  scope: string,
  name: string,
  config: AssetFieldConfig
): void {
  if (!mappingConfig.fields) mappingConfig.fields = {};
  mappingConfig.fields[buildFieldConfigKey(scope, name)] = config;
}

/**
 * Estrae il vero nome del campo (quello usato per leggere il valore dal veicolo/segnale) da una
 * chiave di mappingConfig.fields, che sia nel nuovo formato "scope|name" o nel vecchio formato
 * piatto "name". Usare questo helper ovunque si itera Object.keys(mappingConfig.fields) e si deve
 * risalire al nome reale del campo (es. side-bar-map, actia-vehicle-details, popup mappa).
 */
export function extractNameFromFieldConfigKey(key: string): string {
  const sepIndex = key.indexOf('|');
  return sepIndex === -1 ? key : key.slice(sepIndex + 1);
}

/**
 * Fallback per i (pochi) punti che leggono un campo per solo "name", senza conoscere lo scope
 * (es. chiamate dal template che non lo passano). Restituisce la prima entry trovata con quel
 * name, in un qualunque scope. Per i name NON duplicati (la stragrande maggioranza) è equivalente
 * a un lookup esatto; per i name duplicati fra scope diversi risolve in modo ambiguo (comportamento
 * pre-esistente, non peggiore di quando la chiave era condivisa). Preferire sempre getFieldConfig
 * quando lo scope è disponibile.
 */
export function findFieldConfigByName(
  mappingConfig: AssetMappingConfig | null | undefined,
  name: string
): AssetFieldConfig | undefined {
  if (!mappingConfig?.fields) return undefined;
  if (mappingConfig.fields[name]) return mappingConfig.fields[name];
  const key = Object.keys(mappingConfig.fields).find(k => extractNameFromFieldConfigKey(k) === name);
  return key ? mappingConfig.fields[key] : undefined;
}
